///
/// EarthBrowser
///
/// Created by Matt Giger on September 13, 2026
/// Copyright (c) 2026 Geodata Labs
///

/// Read-only scanning of browser storage (OPFS/VFS, IndexedDB, localStorage)
/// used by the Storage Manager dev tool.

import { logger } from '@/utils/LogDispatcher'

export interface VFSFileInfo {
	name: string
	path: string
	flags: number
	size: number
	dataSize: number
}

export interface VFSVolumeInfo {
	name: string
	files: VFSFileInfo[]
	totalSize: number
	activePaths: number
}

export interface OPFSFileInfo {
	name: string
	size: number
}

export interface IndexedDBStoreInfo {
	name: string
	keyPath: string | string[] | null
	autoIncrement: boolean
	indexCount: number
	recordCount: number
	sampleKeys: IDBValidKey[]
}

export interface IndexedDBInfo {
	name: string
	version: number
	objectStores: IndexedDBStoreInfo[]
}

export interface LocalStorageItem {
	key: string
	value: string
	size: number
}

export interface StorageSnapshot {
	usage: number
	quota: number
	isPersisted: boolean
	opfsTotal: number
	indexedDBEstimate: number
	volumes: VFSVolumeInfo[]
	geopackages: OPFSFileInfo[]
	indexedDBs: IndexedDBInfo[]
	localStorageItems: LocalStorageItem[]
}

// OPFS directory handles support async iteration at runtime, but the DOM lib
// in use doesn't declare it, so cast through a minimal structural type
type DirectoryHandleEntries = AsyncIterable<[string, FileSystemFileHandle | FileSystemDirectoryHandle]>

function directoryEntries(handle: FileSystemDirectoryHandle): DirectoryHandleEntries {
	return handle as unknown as DirectoryHandleEntries
}

async function scanDirectorySize(dirHandle: FileSystemDirectoryHandle): Promise<number> {
	let totalSize = 0
	try {
		for await (const [, handle] of directoryEntries(dirHandle)) {
			if (handle.kind === 'file') {
				const file = await handle.getFile()
				totalSize += file.size
			} else if (handle.kind === 'directory') {
				totalSize += await scanDirectorySize(handle)
			}
		}
	} catch (error) {
		logger.warn('Error scanning directory:', [[error]])
	}
	return totalSize
}

/// VFS files created by AccessHandlePoolVFS have a 4KB header containing the
/// associated SQLite path and open flags
async function scanVFSVolume(volumeName: string, dirHandle: FileSystemDirectoryHandle): Promise<VFSVolumeInfo> {
	const files: VFSFileInfo[] = []
	const HEADER_MAX_PATH_SIZE = 512
	const HEADER_CORPUS_SIZE = HEADER_MAX_PATH_SIZE + 4
	const HEADER_OFFSET_FLAGS = HEADER_MAX_PATH_SIZE
	const HEADER_OFFSET_DATA = 4096

	for await (const [name, handle] of directoryEntries(dirHandle)) {
		if (handle.kind !== 'file') continue
		try {
			const file = await handle.getFile()
			const headerData = await file.slice(0, HEADER_CORPUS_SIZE).arrayBuffer()
			const corpus = new Uint8Array(headerData)
			const pathBytes = corpus.findIndex(value => value === 0)
			const path = new TextDecoder().decode(corpus.subarray(0, pathBytes > 0 ? pathBytes : 0))
			const dataView = new DataView(corpus.buffer)
			const flags = dataView.getUint32(HEADER_OFFSET_FLAGS)

			files.push({
				name,
				path: path || '(unassociated)',
				flags,
				size: file.size,
				dataSize: file.size - HEADER_OFFSET_DATA,
			})
		} catch (error) {
			logger.warn(`Failed to read file ${name}:`, [[error]])
		}
	}

	const activePaths = new Set(files.map(f => f.path).filter(p => p !== '(unassociated)')).size

	return {
		name: volumeName,
		files,
		totalSize: files.reduce((sum, f) => sum + f.size, 0),
		activePaths,
	}
}

async function scanIndexedDBs(): Promise<IndexedDBInfo[]> {
	if (!('indexedDB' in window) || typeof indexedDB.databases !== 'function') {
		return []
	}

	const dbInfos: IndexedDBInfo[] = []
	try {
		const databases = await indexedDB.databases()

		for (const dbInfo of databases) {
			if (!dbInfo.name) continue
			const dbName = dbInfo.name

			try {
				const db = await new Promise<IDBDatabase>((resolve, reject) => {
					const request = indexedDB.open(dbName, dbInfo.version)
					request.onsuccess = () => resolve(request.result)
					request.onerror = () => reject(request.error ?? new Error('Failed to open IndexedDB database'))
				})

				const objectStores = await Promise.all(
					Array.from(db.objectStoreNames).map(async (storeName): Promise<IndexedDBStoreInfo> => {
						const transaction = db.transaction(storeName, 'readonly')
						const store = transaction.objectStore(storeName)

						const countPromise = new Promise<number>((resolve) => {
							const countRequest = store.count()
							countRequest.onsuccess = () => resolve(countRequest.result)
							countRequest.onerror = () => resolve(0)
						})

						const keysPromise = new Promise<IDBValidKey[]>((resolve) => {
							try {
								const keysRequest = store.getAllKeys(undefined, 5)
								keysRequest.onsuccess = () => resolve(keysRequest.result || [])
								keysRequest.onerror = () => resolve([])
							} catch {
								resolve([])
							}
						})

						const [recordCount, sampleKeys] = await Promise.all([countPromise, keysPromise])

						return {
							name: storeName,
							keyPath: store.keyPath,
							autoIncrement: store.autoIncrement,
							indexCount: store.indexNames.length,
							recordCount,
							sampleKeys,
						}
					})
				)

				dbInfos.push({ name: dbName, version: db.version, objectStores })
				db.close()
			} catch (error) {
				logger.warn(`Failed to open database ${dbName}:`, [[error]])
			}
		}
	} catch (error) {
		logger.error('Failed to enumerate IndexedDB databases:', [[error]])
	}

	return dbInfos
}

function scanLocalStorage(): LocalStorageItem[] {
	const items: LocalStorageItem[] = []
	try {
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i)
			if (key) {
				const value = localStorage.getItem(key) || ''
				// Estimate size (key + value in bytes, UTF-16)
				items.push({ key, value, size: (key.length + value.length) * 2 })
			}
		}
		items.sort((a, b) => b.size - a.size)
	} catch (error) {
		logger.error('Failed to load localStorage info:', [[error]])
	}
	return items
}

export async function scanStorage(): Promise<StorageSnapshot> {
	let usage = 0
	let quota = 0
	let isPersisted = false

	if (navigator.storage?.estimate) {
		const estimate = await navigator.storage.estimate()
		usage = estimate.usage || 0
		quota = estimate.quota || 0
		isPersisted = navigator.storage.persisted ? await navigator.storage.persisted() : false
	}

	const volumes: VFSVolumeInfo[] = []
	const geopackages: OPFSFileInfo[] = []
	let opfsTotal = 0

	if (navigator.storage?.getDirectory) {
		const opfsRoot = await navigator.storage.getDirectory()
		for await (const [name, handle] of directoryEntries(opfsRoot)) {
			if (handle.kind === 'directory' && name.startsWith('vfs_')) {
				const volume = await scanVFSVolume(name, handle)
				volumes.push(volume)
				opfsTotal += volume.totalSize
			} else if (handle.kind === 'directory' && name === 'geopackages') {
				for await (const [fileName, fileHandle] of directoryEntries(handle)) {
					if (fileHandle.kind === 'file') {
						const file = await fileHandle.getFile()
						geopackages.push({ name: fileName, size: file.size })
						opfsTotal += file.size
					}
				}
			} else if (handle.kind === 'directory') {
				opfsTotal += await scanDirectorySize(handle)
			} else if (handle.kind === 'file') {
				const file = await handle.getFile()
				opfsTotal += file.size
			}
		}
	}

	const indexedDBs = await scanIndexedDBs()
	const localStorageItems = scanLocalStorage()

	return {
		usage,
		quota,
		isPersisted,
		opfsTotal,
		// IndexedDB size isn't directly measurable; estimate as remainder
		indexedDBEstimate: Math.max(0, usage - opfsTotal),
		volumes,
		geopackages,
		indexedDBs,
		localStorageItems,
	}
}

export function formatBytes(bytes: number): string {
	if (bytes === 0) return '0 B'
	const k = 1024
	const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
	const i = Math.floor(Math.log(bytes) / Math.log(k))
	return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
}

export function formatFlags(flags: number): string {
	const flagNames = []
	if (flags & 0x00000001) flagNames.push('READONLY')
	if (flags & 0x00000002) flagNames.push('READWRITE')
	if (flags & 0x00000004) flagNames.push('CREATE')
	if (flags & 0x00000100) flagNames.push('MAIN_DB')
	if (flags & 0x00000200) flagNames.push('TEMP_DB')
	if (flags & 0x00000400) flagNames.push('TRANSIENT_DB')
	if (flags & 0x00000800) flagNames.push('MAIN_JOURNAL')
	return flagNames.length > 0 ? flagNames.join(' | ') : `0x${flags.toString(16)}`
}

const VOLUME_LABELS: Record<string, string> = {
	vfs_eb_volumes: 'Main Database',
	vfs_imports: 'Imports',
	vfs_exports: 'Exports',
}

export function formatVolumeName(volumeName: string): string {
	if (VOLUME_LABELS[volumeName]) return VOLUME_LABELS[volumeName]
	if (volumeName.startsWith('vfs_gpkg_')) return `GeoPackage: ${volumeName.slice('vfs_gpkg_'.length)}`
	return volumeName
}

export function volumeSortOrder(volumeName: string): number {
	if (volumeName === 'vfs_eb_volumes') return 0
	if (volumeName === 'vfs_imports') return 1
	if (volumeName === 'vfs_exports') return 2
	return 999
}
